import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomSheet } from './BottomSheet';
import {
  registerConnectionPromptHandler,
  CONNECTION_PROVIDER_LABELS,
  type ConnectionPromptRequest,
  type ConnectionPromptDecision,
} from '../lib/connectionsBridge';

/**
 * Global "connect your account first" prompt. Mounted once in Layout; the API
 * layer raises it via promptConnectionRequired() whenever a write is rejected
 * with 412 connection_required (the action would post as the user, but they
 * haven't linked that integration account yet).
 *
 * When the backend marks the action as `globalFallbackAvailable` (an admin
 * enabled the shared-connection fallback), the sheet also offers "Use shared
 * connection" — choosing it makes the API layer retry the SAME write on the
 * shared/global connection for this one action.
 */
export function ConnectionRequiredSheet() {
  const [req, setReq] = useState<ConnectionPromptRequest | null>(null);
  const resolveRef = useRef<((d: ConnectionPromptDecision) => void) | null>(null);
  const navigate = useNavigate();

  // Register the bridge handler once for the app's lifetime (mirrors authBridge).
  useEffect(() => {
    registerConnectionPromptHandler((r, resolve) => {
      // A new prompt while one is open: resolve the stale one as cancelled first.
      resolveRef.current?.('cancel');
      resolveRef.current = resolve;
      setReq(r);
    });
    // Defensive: if this ever unmounts with a decision pending, resolve it as
    // 'cancel' so the awaiting api request() rejects instead of hanging forever.
    return () => { resolveRef.current?.('cancel'); resolveRef.current = null; };
  }, []);

  // Resolve the pending promise with the user's decision and close the sheet.
  const decide = (decision: ConnectionPromptDecision) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setReq(null);
    resolve?.(decision);
  };

  const label = req ? CONNECTION_PROVIDER_LABELS[req.provider] : '';
  const canFallback = req?.globalFallbackAvailable === true;

  return (
    <BottomSheet
      open={req !== null}
      onClose={() => decide('cancel')}
      title={`Connect ${label}`}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => decide('cancel')}>
            Cancel
          </button>
          {canFallback && (
            <button className="btn btn-secondary" onClick={() => decide('use-global')}>
              Use shared connection
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={() => {
              decide('connect');
              navigate('/profile#connections');
            }}
          >
            Connect in Profile
          </button>
        </div>
      }
    >
      <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
        {canFallback
          ? `This action posts as you, but your ${label} account isn't connected. Connect it in Profile, or use the shared ${label} connection just for this action.`
          : `This action posts as you — connect your ${label} account first.`}
      </p>
    </BottomSheet>
  );
}
